import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function FiveEInstructionalModelPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
      isActive: true,
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="5E instructional model"
        description="The 5E instructional model moves students through five phases to build and further develop understanding. These phases are: Engage → Explore → Explain → Elaborate → Evaluate."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'Biological Sciences Curriculum Study (BSCS), Colorado, USA',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                In 2023, Polanin et al. from the American Institutes for Research conducted a
                meta-analysis of 61 randomized control trial studies and found that the 5E
                instructional model had an effect size on student science achievement of 0.87.
                Download the research paper{' '}
                <Link
                  url="https://journals.sagepub.com/doi/pdf/10.1177/23328584241269866"
                  openInNewTab
                >
                  here
                </Link>
                .
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link
                url="https://bscs.org/reports/the-bscs-5e-instructional-model-origins-and-effectiveness/"
                openInNewTab
              >
                BSCS 5E Instructional Model white paper
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Every Stile unit is informed by the 5E instructional model at a program, unit and lesson level.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="engage-example"
            title="Engage: stoking student interest"
            description="A live poll about students' earthquake experiences sparks curiosity and surfaces prior conceptions."
            url="http://stileapp.com/go/5es_example_1"
            subtitle={
              <div>
                <em>Introduction: Natural disasters</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/5e_engage.png"
                alt="Engage phase example"
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="explore-example"
            title="Explore: active investigations"
            description="Students investigate why sunlight is weaker in winter by experimenting with flashlights and angles."
            url="http://stileapp.com/go/5es_example_2"
            subtitle={
              <div>
                <em>Modeling sunlight intensity</em> lesson
                <br />
                in the Our Place in Space unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/5e_explore.png"
                alt="Text: Investigate how the intensity of light on a surface depends on the angle of the light beam. Image: Students shining a torch at different angles onto graph paper using a protractor, measuring and drawing the light circles."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="explain-example"
            title="Explain: clarifying concepts"
            description="Students draw on learning experiences from the previous phases to explain key concepts. Teachers use these explanations to gauge student understanding and inform next teaching steps."
            url="http://stileapp.com/go/5es_example_3"
            subtitle={
              <div>
                <em>The fossil record</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/5e_explain_AU.png"
                alt="Text: Summarise how the dinosaur fossils formed. Image: Illustration of buried dinosaur skeletons and remains in soil and water layers."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="elaborate-example"
            title="Elaborate: applying and extending knowledge"
            description="Students apply their knowledge of forces in a novel design task, demonstrating their grasp of the concept by extending it to a new context."
            url="http://stileapp.com/go/5es_example_4"
            subtitle={
              <div>
                <em>Long-distance communication</em> lesson
                <br />
                in the Waves unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/5e_elaborate.png"
                alt="Text: Discuss whether satellite internet could provide fairer internet access in the future. Image: Two students on a video call with Earth and an orbiting satellite in the background."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="evaluate-example"
            title="Evaluate: assessment and reflection"
            description="Students receive feedback on their thinking in this final phase. Teach Mode allows on-the-spot formative assessment during lessons, auto-marked check-ins provide prompt analysis of student understanding and summative tests offer a detailed view of student learning."
            url="http://stileapp.com/go/5es_example_5"
            subtitle={
              <div>
                <em>Test: The Hydrosphere</em> lesson
                <br />
                in The Hydrosphere unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/5e_evaluate.png"
                alt="Text: Label the features to complete the diagram. Image: Diagram of a landscape with rivers, lake, clouds, and groundwater, with labels to be placed on features."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: 'Evidence of Success',
            description: 'How Stile has a positive impact in the science classroom.',
            url: '/why-choose-stile/evidence-of-success/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default FiveEInstructionalModelPage;
